import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Invoice from "../components/admin/Invoice";
import {
  getCategories,
  getPackages,
  getAddOns,
  createBooking,
  subscribeToBookings,
  subscribeToPendingPayments,
  getBookingById,
  verifyPayment,
  rejectPayment,
  cancelBooking,
  markBookingCompleted,
  updateBooking,
  deleteBooking,
  addPayment,
} from "../services/firestore";
import { services as mockServices } from "../mock";
import {
  BOOKING_STATUS,
  PAYMENT_STATUS,
  formatCurrency,
  formatDateShort,
  getStatusLabel,
  getPaymentStatusLabel,
  getPaymentTypeLabel,
  STATUS_COLORS,
  PAYMENT_STATUS_COLORS,
} from "../types/booking";
import {
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Search,
  Eye,
  X,
  Loader2,
  Plus,
  Edit2,
  Trash2,
  CreditCard,
} from "lucide-react";
import { toastSuccess, toastError, toastPromise } from "../components/admin/ToastProvider";
import { useConfirm, ConfirmDialog } from "../components/admin/ConfirmDialog";
import { isSameDay } from "date-fns";

const AdminBookingManagement = () => {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  // Confirmation dialog hook
  const { confirm, ConfirmDialog } = useConfirm();

  const [bookings, setBookings] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [packages, setPackages] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [selectedServiceCategory, setSelectedServiceCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [actionLoading, setActionLoading] = useState(false);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);

  // Form states
  const [bookingForm, setBookingForm] = useState({
    clientName: "",
    clientEmail: "",
    clientPhone: "",
    clientInstagram: "",
    eventDate: "",
    eventTime: "",
    eventLocation: "",
    hennaDate: "",
    hennaTime: "",
    sameAsEventDate: true,
    specialRequests: "",
    items: [],
    depositPercent: 30,
    serviceCategory: "",
    subcategory: "",
    package: null,
  });

  // Status update form
  const [statusForm, setStatusForm] = useState({
    newStatus: "",
    reason: "",
  });

  // Payment form
  const [paymentForm, setPaymentForm] = useState({
    paymentType: "DEPOSIT",
    amount: "",
    paymentMethod: "TRANSFER",
    paymentChannel: "",
    transactionRef: "",
    notes: "",
  });

  // Redirect if not admin
  useEffect(() => {
    if (!isAdmin) {
      navigate("/admin");
    }
  }, [isAdmin, navigate]);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [cats, pkgs, aos] = await Promise.all([
          getCategories(),
          getPackages(),
          getAddOns(),
        ]);
        setCategories(cats);
        setPackages(pkgs);
        setAddOns(aos);

        // Set default service category if services exist
        if (mockServices && mockServices.length > 0) {
          setSelectedServiceCategory(mockServices[0]);
        }
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };
    loadData();
  }, []);

  // Subscribe to bookings and payments
  useEffect(() => {
    const unsubBookings = subscribeToBookings((data) => {
      setBookings(data);
      setLoading(false);
    });

    const unsubPayments = subscribeToPendingPayments((data) => {
      setPendingPayments(data);
    });

    return () => {
      unsubBookings();
      unsubPayments();
    };
  }, []);

  // Filter bookings
  const filteredBookings = bookings.filter((booking) => {
    const matchesSearch =
      booking.bookingNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.clientEmail?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || booking.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Reset form
  const resetForm = () => {
    setBookingForm({
      clientName: "",
      clientEmail: "",
      clientPhone: "",
      clientInstagram: "",
      eventDate: "",
      eventTime: "",
      eventLocation: "",
      hennaDate: "",
      hennaTime: "",
      sameAsEventDate: true,
      specialRequests: "",
      items: [],
      depositPercent: 30,
      serviceCategory: "",
      subcategory: "",
      package: null,
    });
    setSelectedServiceCategory(mockServices?.[0] || null);
    setSelectedSubcategory(null);
    setEditingBooking(null);
  };

  // Toggle item selection (add/remove)
  const toggleItem = (packageData) => {
    const existingIndex = bookingForm.items.findIndex(
      (item) => item.packageId === packageData.id
    );

    if (existingIndex >= 0) {
      // Remove item if already exists
      setBookingForm({
        ...bookingForm,
        items: bookingForm.items.filter((item) => item.packageId !== packageData.id),
      });
    } else {
      // Add new item
      setBookingForm({
        ...bookingForm,
        items: [
          ...bookingForm.items,
          {
            type: "PACKAGE",
            packageId: packageData.id,
            packageSnapshot: packageData,
            price: packageData.price,
            quantity: 1,
          },
        ],
      });
    }
  };

  // Check if package is selected
  const isPackageSelected = (packageId) => {
    return bookingForm.items.some((item) => item.packageId === packageId);
  };

  // Add booking
  const handleAddBooking = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Validate package selection
      if (!bookingForm.items || bookingForm.items.length === 0) {
        toastError("Silakan pilih minimal 1 paket henna!");
        setSubmitting(false);
        return;
      }

      const bookingData = {
        ...bookingForm,
        eventDate: new Date(bookingForm.eventDate),
        hennaDate: bookingForm.sameAsEventDate
          ? new Date(bookingForm.eventDate)
          : new Date(bookingForm.hennaDate),
        hennaTime: bookingForm.sameAsEventDate
          ? bookingForm.eventTime
          : bookingForm.hennaTime,
        source: "admin",
        createdBy: user?.uid,
      };

      await toastPromise(
        createBooking(bookingData),
        {
          loading: "Membuat booking...",
          success: "Booking berhasil dibuat!",
          error: "Gagal membuat booking",
        }
      );
      resetForm();
      setShowAddModal(false);
    } catch (error) {
      console.error("Error creating booking:", error);
      toastError(error.message || "Gagal membuat booking. Silakan coba lagi.");
    } finally {
      setSubmitting(false);
    }
  };

  // Edit booking
  const handleEditBooking = async (booking) => {
    setEditingBooking(booking);

    // Convert eventDate to YYYY-MM-DD format for date input
    let formattedDate = "";
    if (booking.eventDate) {
      const date = booking.eventDate.toDate
        ? booking.eventDate.toDate()
        : new Date(booking.eventDate);
      formattedDate = date.toISOString().split("T")[0];
    }

    // Convert hennaDate to YYYY-MM-DD format for date input
    let formattedHennaDate = "";
    if (booking.hennaDate) {
      const hennaDate = booking.hennaDate.toDate
        ? booking.hennaDate.toDate()
        : new Date(booking.hennaDate);
      formattedHennaDate = hennaDate.toISOString().split("T")[0];
    }

    // Check if henna date is same as event date
    const isSameDate = booking.hennaDate && booking.eventDate
      ? formattedHennaDate === formattedDate
      : true; // Default to true for backward compatibility

    setBookingForm({
      clientName: booking.clientName,
      clientEmail: booking.clientEmail,
      clientPhone: booking.clientPhone,
      clientInstagram: booking.clientInstagram || "",
      eventDate: formattedDate,
      eventTime: booking.eventTime || "",
      eventLocation: booking.eventLocation || "",
      hennaDate: formattedHennaDate,
      hennaTime: booking.hennaTime || booking.eventTime || "",
      sameAsEventDate: isSameDate,
      specialRequests: booking.specialRequests || "",
      items: booking.items || [],
      depositPercent: booking.depositPercent || 30,
    });
    setShowEditModal(true);
  };

  const handleUpdateBooking = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await toastPromise(
        updateBooking(editingBooking.id, {
          ...bookingForm,
          eventDate: new Date(bookingForm.eventDate),
          hennaDate: bookingForm.sameAsEventDate
            ? new Date(bookingForm.eventDate)
            : new Date(bookingForm.hennaDate),
          hennaTime: bookingForm.sameAsEventDate
            ? bookingForm.eventTime
            : bookingForm.hennaTime,
        }),
        {
          loading: "Memperbarui booking...",
          success: "Booking berhasil diperbarui!",
          error: "Gagal memperbarui booking",
        }
      );
      resetForm();
      setShowEditModal(false);
      setEditingBooking(null);
    } catch (error) {
      console.error("Error updating booking:", error);
      toastError("Gagal mengupdate booking. Silakan coba lagi.");
    } finally {
      setSubmitting(false);
    }
  };

  // View booking details
  const handleViewDetails = async (booking) => {
    setSelectedBooking(booking);
    setLoadingDetails(true);

    try {
      const details = await getBookingById(booking.id);
      setBookingDetails(details);
    } catch (error) {
      console.error("Error fetching booking details:", error);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Verify payment
  const handleVerifyPayment = async (bookingId, paymentId) => {
    const confirmed = await confirm(
      "Konfirmasi pembayaran ini? Pastikan bukti pembayaran sudah valid.",
      {
        title: "Verifikasi Pembayaran",
        type: "success",
        confirmText: "Ya, Verifikasi",
        cancelText: "Batal",
      }
    );

    if (!confirmed) return;

    setActionLoading(true);
    try {
      await toastPromise(
        verifyPayment(bookingId, paymentId, user?.uid),
        {
          loading: "Memverifikasi pembayaran...",
          success: "Pembayaran berhasil diverifikasi!",
          error: "Gagal memverifikasi pembayaran",
        }
      );
      await handleViewDetails(selectedBooking);
    } catch (error) {
      console.error("Error verifying payment:", error);
      toastError("Gagal memverifikasi pembayaran. Silakan coba lagi.");
    } finally {
      setActionLoading(false);
    }
  };

  // Reject payment
  const handleRejectPayment = async (bookingId, paymentId) => {
    const reason = prompt("Alasan penolakan:");
    if (!reason) return;

    setActionLoading(true);
    try {
      await toastPromise(
        rejectPayment(bookingId, paymentId, reason),
        {
          loading: "Menolak pembayaran...",
          success: "Pembayaran berhasil ditolak!",
          error: "Gagal menolak pembayaran",
        }
      );
      await handleViewDetails(selectedBooking);
    } catch (error) {
      console.error("Error rejecting payment:", error);
      toastError("Gagal menolak pembayaran. Silakan coba lagi.");
    } finally {
      setActionLoading(false);
    }
  };

  // Cancel booking
  const handleCancelBooking = async (bookingId) => {
    const reason = prompt("Alasan pembatalan:");
    if (!reason) return;

    setActionLoading(true);
    try {
      await toastPromise(
        cancelBooking(bookingId, reason),
        {
          loading: "Membatalkan booking...",
          success: "Booking berhasil dibatalkan!",
          error: "Gagal membatalkan booking",
        }
      );
      setSelectedBooking(null);
      setBookingDetails(null);
    } catch (error) {
      console.error("Error cancelling booking:", error);
      toastError("Gagal membatalkan booking. Silakan coba lagi.");
    } finally {
      setActionLoading(false);
    }
  };

  // Mark as completed
  const handleMarkCompleted = async (bookingId) => {
    const confirmed = await confirm(
      "Tandai booking ini sebagai selesai?",
      {
        title: "Tandai Selesai",
        type: "success",
        confirmText: "Ya, Tandai Selesai",
        cancelText: "Batal",
      }
    );

    if (!confirmed) return;

    setActionLoading(true);
    try {
      await toastPromise(
        markBookingCompleted(bookingId),
        {
          loading: "Menandai booking sebagai selesai...",
          success: "Booking berhasil ditandai selesai!",
          error: "Gagal mengupdate booking",
        }
      );
      setSelectedBooking(null);
      setBookingDetails(null);
    } catch (error) {
      console.error("Error marking booking as completed:", error);
      toastError("Gagal mengupdate booking. Silakan coba lagi.");
    } finally {
      setActionLoading(false);
    }
  };

  // Delete booking
  const handleDeleteBooking = async (booking) => {
    const confirmed = await confirm(
      `Yakin ingin menghapus booking ${booking.bookingNumber}? Tindakan ini tidak dapat dibatalkan.`,
      {
        title: "Hapus Booking",
        type: "danger",
        confirmText: "Ya, Hapus",
        cancelText: "Batal",
      }
    );

    if (!confirmed) return;

    setActionLoading(true);
    try {
      await toastPromise(
        deleteBooking(booking.id),
        {
          loading: "Menghapus booking...",
          success: "Booking berhasil dihapus!",
          error: "Gagal menghapus booking",
        }
      );
    } catch (error) {
      console.error("Error deleting booking:", error);
      toastError("Gagal menghapus booking. Silakan coba lagi.");
    } finally {
      setActionLoading(false);
    }
  };

  // Update status
  const handleUpdateStatus = async () => {
    if (!statusForm.newStatus) {
      toastError("Pilih status baru!");
      return;
    }

    if (
      statusForm.newStatus === BOOKING_STATUS.CANCELLED &&
      !statusForm.reason
    ) {
      toastError("Mohon isi alasan pembatalan!");
      return;
    }

    setActionLoading(true);
    try {
      if (statusForm.newStatus === BOOKING_STATUS.CANCELLED) {
        await cancelBooking(bookingDetails.id, statusForm.reason);
      } else if (statusForm.newStatus === BOOKING_STATUS.CONFIRMED) {
        await updateBooking(bookingDetails.id, {
          status: BOOKING_STATUS.CONFIRMED,
        });
      } else if (statusForm.newStatus === BOOKING_STATUS.COMPLETED) {
        await markBookingCompleted(bookingDetails.id);
      }
      toastSuccess("Status berhasil diupdate!");
      setShowStatusModal(false);
      setStatusForm({ newStatus: "", reason: "" });
      await handleViewDetails(selectedBooking);
    } catch (error) {
      console.error("Error updating status:", error);
      toastError("Gagal mengupdate status. Silakan coba lagi.");
    } finally {
      setActionLoading(false);
    }
  };

  // Quick status change (direct from table)
  const handleQuickStatusChange = async (bookingId, newStatus) => {
    let confirmMessage = "";
    let confirmTitle = "";
    let needsReason = false;
    let confirmType = "success";

    switch (newStatus) {
      case BOOKING_STATUS.CONFIRMED:
        confirmMessage = "Konfirmasi booking ini? Pastikan deposit sudah dibayar.";
        confirmTitle = "Konfirmasi Booking";
        break;
      case BOOKING_STATUS.COMPLETED:
        confirmMessage = "Tandai booking sebagai selesai?";
        confirmTitle = "Tandai Selesai";
        break;
      case BOOKING_STATUS.CANCELLED:
        confirmMessage = "Batalkan booking ini?";
        confirmTitle = "Batalkan Booking";
        needsReason = true;
        confirmType = "danger";
        break;
      default:
        return;
    }

    const confirmed = await confirm(confirmMessage, {
      title: confirmTitle,
      type: confirmType,
      confirmText: "Ya",
      cancelText: "Batal",
    });

    if (!confirmed) return;

    let reason = "";
    if (needsReason) {
      reason = prompt("Alasan pembatalan:");
      if (!reason) return;
    }

    setActionLoading(true);
    try {
      if (newStatus === BOOKING_STATUS.CANCELLED) {
        await cancelBooking(bookingId, reason);
      } else if (newStatus === BOOKING_STATUS.CONFIRMED) {
        await updateBooking(bookingId, { status: newStatus });
      } else if (newStatus === BOOKING_STATUS.COMPLETED) {
        await markBookingCompleted(bookingId);
      }
      toastSuccess("Status berhasil diupdate!");
    } catch (error) {
      console.error("Error updating status:", error);
      toastError("Gagal mengupdate status. Silakan coba lagi.");
    } finally {
      setActionLoading(false);
    }
  };

  // Add manual payment
  const handleAddPayment = async (e) => {
    e.preventDefault();

    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toastError("Masukkan jumlah pembayaran yang valid!");
      return;
    }

    const paymentAmount = parseFloat(paymentForm.amount);
    const remainingAmount = bookingDetails.remainingAmount || 0;

    // Validasi: jumlah pembayaran tidak boleh melebihi sisa pembayaran
    if (paymentAmount > remainingAmount) {
      toastError(
        `Jumlah pembayaran melebihi sisa pembayaran! Sisa: ${formatCurrency(remainingAmount)}`
      );
      return;
    }

    setActionLoading(true);
    try {
      await toastPromise(
        addPayment(bookingDetails.id, {
          paymentType: paymentForm.paymentType,
          amount: paymentAmount,
          paymentMethod: paymentForm.paymentMethod,
          paymentChannel: paymentForm.paymentChannel || null,
          transactionReference: paymentForm.transactionRef || null,
          notes: paymentForm.notes || null,
          status: "VERIFIED",
          verifiedBy: user?.uid,
        }),
        {
          loading: "Menambahkan pembayaran...",
          success: "Pembayaran berhasil ditambahkan!",
          error: "Gagal menambahkan pembayaran",
        }
      );
      setShowPaymentModal(false);
      setPaymentForm({
        paymentType: "DEPOSIT",
        amount: "",
        paymentMethod: "TRANSFER",
        paymentChannel: "",
        transactionRef: "",
        notes: "",
      });
      await handleViewDetails(selectedBooking);
    } catch (error) {
      console.error("Error adding payment:", error);
      toastError("Gagal menambahkan pembayaran. Silakan coba lagi.");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#D4AF7A] animate-spin mx-auto" />
          <p className="mt-4 text-[#F7F5F2]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Confirmation Dialog */}
      <ConfirmDialog />

      {/* Main Content */}
      <main className="mx-auto max-w-7xl">
        {/* Stats */}
        <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#F7F5F2]/70">Total Booking</p>
                <p className="text-2xl font-bold text-[#F7F5F2]">
                  {bookings.length}
                </p>
              </div>
              <Calendar className="w-8 h-8 text-[#D4AF7A]" />
            </div>
          </div>

          <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#F7F5F2]/70">Menunggu DP</p>
                <p className="text-2xl font-bold text-yellow-400">
                  {
                    bookings.filter(
                      (b) => b.status === BOOKING_STATUS.PENDING_DEPOSIT,
                    ).length
                  }
                </p>
              </div>
              <Clock className="w-8 h-8 text-yellow-400" />
            </div>
          </div>

          <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#F7F5F2]/70">Dikonfirmasi</p>
                <p className="text-2xl font-bold text-green-400">
                  {
                    bookings.filter(
                      (b) => b.status === BOOKING_STATUS.CONFIRMED,
                    ).length
                  }
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
          </div>

          <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#F7F5F2]/70">Payment Pending</p>
                <p className="text-2xl font-bold text-red-400">
                  {pendingPayments.length}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
          </div>
        </div>

        {/* Filters & Actions */}
        <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-4 mb-6">
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#F7F5F2]/50" />
                <input
                  type="text"
                  placeholder="Cari booking number, nama, email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                />
              </div>
            </div>
            <div className="w-full sm:w-64">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] focus:outline-none focus:border-[#D4AF7A]"
              >
                <option value="all">Semua Status</option>
                <option value={BOOKING_STATUS.PENDING_DEPOSIT}>
                  Menunggu DP
                </option>
                <option value={BOOKING_STATUS.CONFIRMED}>Dikonfirmasi</option>
                <option value={BOOKING_STATUS.COMPLETED}>Selesai</option>
                <option value={BOOKING_STATUS.CANCELLED}>Dibatalkan</option>
              </select>
            </div>
            <button
              onClick={() => {
                resetForm();
                setShowAddModal(true);
              }}
              className="flex shrink-0 items-center text-sm justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:opacity-90 transition-opacity"
            >
              <Plus size={18} />
              Tambah Booking
            </button>
          </div>
        </div>

        {/* Bookings Table */}
        <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-[#F7F5F2]/70 uppercase">
                    Booking #
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-[#F7F5F2]/70 uppercase">
                    Klien
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-[#F7F5F2]/70 uppercase">
                    Item
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-[#F7F5F2]/70 uppercase hidden sm:table-cell">
                    Tanggal
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-[#F7F5F2]/70 uppercase hidden md:table-cell">
                    Status
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-[#F7F5F2]/70 uppercase hidden sm:table-cell">
                    Total
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-[#F7F5F2]/70 uppercase">
                    Aksi
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#D4AF7A]/10">
                {filteredBookings.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-12 text-center text-[#F7F5F2]/70"
                    >
                      Tidak ada booking ditemukan
                    </td>
                  </tr>
                ) : (
                  filteredBookings.map((booking) => (
                    <tr
                      key={booking.id}
                      onClick={() => handleViewDetails(booking)}
                      className="cursor-pointer hover:bg-white/5"
                    >
                      <td className="px-4 sm:px-6 py-4 text-sm font-medium text-[#F7F5F2]">
                        {booking.bookingNumber}
                      </td>
                      <td className="px-4 py-4 sm:px-6">
                        <div className="text-sm font-medium text-[#F7F5F2]">
                          {booking.clientName}
                        </div>
                        <div className="flex flex-wrap items-center gap-1 mt-1 sm:hidden">
                          <span className="text-xs text-[#F7F5F2]/70">
                            {formatDateShort(booking.eventDate)}
                          </span>
                          <span
                            className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_COLORS[booking.status]}`}
                          >
                            {getStatusLabel(booking.status)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 sm:px-6">
                        <div className="flex flex-col">
                          <div className="text-sm text-[#F7F5F2]">
                            {booking.itemCount || 0} item
                          </div>
                          {booking.items && booking.items.length > 0 && (
                            <div className="text-xs text-[#D4AF7A] truncate max-w-[120px] sm:max-w-[200px]">
                              {booking.items[0].packageSnapshot?.name ||
                                booking.items[0].addOnSnapshot?.name ||
                                "-"}
                              {booking.items.length > 1 &&
                                ` +${booking.items.length - 1}`}
                            </div>
                          )}
                          <div className="text-xs font-medium text-[#D4AF7A] sm:hidden mt-1">
                            {formatCurrency(booking.totalAmount)}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-sm text-[#F7F5F2] hidden sm:table-cell">
                        {formatDateShort(booking.eventDate)}
                      </td>
                      <td className="hidden px-4 py-4 sm:px-6 md:table-cell">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[booking.status]}`}
                        >
                          {getStatusLabel(booking.status)}
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-sm text-[#F7F5F2] hidden sm:table-cell">
                        {formatCurrency(booking.totalAmount)}
                      </td>
                      <td className="px-4 py-4 sm:px-6">
                        <div
                          className="flex items-center gap-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => handleViewDetails(booking)}
                            className="p-2 bg-[#D4AF7A]/10 text-[#D4AF7A] rounded-lg hover:bg-[#D4AF7A]/20 transition-all sm:hidden items-center justify-center flex"
                            title="Detail"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEditBooking(booking)}
                            className="flex items-center justify-center p-2 text-blue-400 transition-all rounded-lg bg-blue-500/10 hover:bg-blue-500/20"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          {/* Quick Status Change Buttons */}
                          {booking.status === BOOKING_STATUS.PENDING_DEPOSIT && (
                            <button
                              onClick={() => handleQuickStatusChange(booking.id, BOOKING_STATUS.CONFIRMED)}
                              disabled={actionLoading}
                              className="flex items-center justify-center p-2 text-green-400 transition-all rounded-lg bg-green-500/10 hover:bg-green-500/20 disabled:opacity-50"
                              title="Konfirmasi"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          {booking.status === BOOKING_STATUS.CONFIRMED && (
                            <button
                              onClick={() => handleQuickStatusChange(booking.id, BOOKING_STATUS.COMPLETED)}
                              disabled={actionLoading}
                              className="flex items-center justify-center p-2 text-emerald-400 transition-all rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 disabled:opacity-50"
                              title="Tandai Selesai"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          {booking.status !== BOOKING_STATUS.CANCELLED && booking.status !== BOOKING_STATUS.COMPLETED && (
                            <button
                              onClick={() => handleQuickStatusChange(booking.id, BOOKING_STATUS.CANCELLED)}
                              disabled={actionLoading}
                              className="flex items-center justify-center p-2 text-red-400 transition-all rounded-lg bg-red-500/10 hover:bg-red-500/20 disabled:opacity-50"
                              title="Batalkan"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}
                          {booking.status === BOOKING_STATUS.CANCELLED && (
                            <button
                              onClick={() => handleDeleteBooking(booking)}
                              className="flex items-center justify-center p-2 text-red-400 transition-all rounded-lg bg-red-500/10 hover:bg-red-500/20"
                              title="Hapus"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pending Payments Alert */}
        {pendingPayments.length > 0 && (
          <div className="p-4 mt-6 border bg-red-500/10 border-red-500/30 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 text-red-400" />
                <h3 className="font-semibold text-red-400">
                  {pendingPayments.length} Pembayaran Menunggu Verifikasi
                </h3>
              </div>
            </div>
            <div className="space-y-2">
              {pendingPayments.slice(0, 3).map((payment) => (
                <div
                  key={payment.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-white/5"
                >
                  <div>
                    <p className="text-sm font-medium text-[#F7F5F2]">
                      {payment.paymentNumber}
                    </p>
                    <p className="text-xs text-[#F7F5F2]/70">
                      {formatCurrency(payment.amount)} - {payment.paymentMethod}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      const booking = bookings.find(
                        (b) => b.id === payment.bookingId,
                      );
                      if (booking) handleViewDetails(booking);
                    }}
                    className="px-3 py-1 bg-[#D4AF7A]/10 text-[#D4AF7A] rounded-lg hover:bg-[#D4AF7A]/20 transition-all text-sm"
                  >
                    Lihat
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Add Booking Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-[#0A0A0A] border border-[#D4AF7A]/30 rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#F7F5F2]">
                Tambah Booking Baru
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="p-2 transition-colors rounded-lg hover:bg-white/10"
              >
                <X className="w-5 h-5 text-[#F7F5F2]" />
              </button>
            </div>

            <form onSubmit={handleAddBooking} className="space-y-6">
              {/* Client Information */}
              <div>
                <h3 className="text-lg font-semibold text-[#F7F5F2] mb-4">
                  Informasi Klien
                </h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Nama Lengkap *
                    </label>
                    <input
                      type="text"
                      required
                      value={bookingForm.clientName}
                      onChange={(e) =>
                        setBookingForm({
                          ...bookingForm,
                          clientName: e.target.value,
                        })
                      }
                      placeholder="Nama klien"
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={bookingForm.clientEmail}
                      onChange={(e) =>
                        setBookingForm({
                          ...bookingForm,
                          clientEmail: e.target.value,
                        })
                      }
                      placeholder="email@example.com"
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      No. WhatsApp *
                    </label>
                    <input
                      type="tel"
                      required
                      value={bookingForm.clientPhone}
                      onChange={(e) =>
                        setBookingForm({
                          ...bookingForm,
                          clientPhone: e.target.value,
                        })
                      }
                      placeholder="08xxxxxxxxxx"
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Instagram
                    </label>
                    <input
                      type="text"
                      value={bookingForm.clientInstagram}
                      onChange={(e) =>
                        setBookingForm({
                          ...bookingForm,
                          clientInstagram: e.target.value,
                        })
                      }
                      placeholder="@username"
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                </div>
              </div>

              {/* Event Information */}
              <div>
                <h3 className="text-lg font-semibold text-[#F7F5F2] mb-4">
                  Informasi Acara
                </h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Tanggal Acara *
                    </label>
                    <input
                      type="date"
                      required
                      value={bookingForm.eventDate}
                      onChange={(e) =>
                        setBookingForm({
                          ...bookingForm,
                          eventDate: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Jam Mulai *
                    </label>
                    <input
                      type="time"
                      required
                      value={bookingForm.eventTime}
                      onChange={(e) =>
                        setBookingForm({
                          ...bookingForm,
                          eventTime: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Lokasi Acara *
                    </label>
                    <input
                      type="text"
                      required
                      value={bookingForm.eventLocation}
                      onChange={(e) =>
                        setBookingForm({
                          ...bookingForm,
                          eventLocation: e.target.value,
                        })
                      }
                      placeholder="Alamat lengkap lokasi acara"
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Deposit %
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={bookingForm.depositPercent}
                      onChange={(e) =>
                        setBookingForm({
                          ...bookingForm,
                          depositPercent: parseInt(e.target.value),
                        })
                      }
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                </div>
              </div>

              {/* Henna Process Information */}
              <div>
                <h3 className="text-lg font-semibold text-[#F7F5F2] mb-4">
                  Informasi Proses Henna
                </h3>

                {/* Sync checkbox */}
                <div className="mb-4">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={bookingForm.sameAsEventDate}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setBookingForm({
                          ...bookingForm,
                          sameAsEventDate: checked,
                          hennaDate: checked ? bookingForm.eventDate : bookingForm.hennaDate,
                          hennaTime: checked ? bookingForm.eventTime : bookingForm.hennaTime,
                        });
                      }}
                      className="w-4 h-4 text-[#D4AF7A] rounded focus:ring-2 focus:ring-[#D4AF7A]/50"
                    />
                    <span className="text-sm text-[#F7F5F2] group-hover:text-[#D4AF7A] transition-colors">
                      Samakan dengan tanggal acara
                    </span>
                  </label>
                </div>

                {/* Henna Date & Time */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Tanggal Proses Henna *
                    </label>
                    <input
                      type="date"
                      required
                      value={bookingForm.sameAsEventDate ? bookingForm.eventDate : bookingForm.hennaDate}
                      onChange={(e) =>
                        setBookingForm({
                          ...bookingForm,
                          hennaDate: e.target.value,
                        })
                      }
                      disabled={bookingForm.sameAsEventDate}
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Jam Proses Henna *
                    </label>
                    <input
                      type="time"
                      required
                      value={bookingForm.sameAsEventDate ? bookingForm.eventTime : bookingForm.hennaTime}
                      onChange={(e) =>
                        setBookingForm({
                          ...bookingForm,
                          hennaTime: e.target.value,
                        })
                      }
                      disabled={bookingForm.sameAsEventDate}
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                </div>
              </div>

              {/* Package Selection */}
              <div>
                <h3 className="text-lg font-semibold text-[#F7F5F2] mb-4">
                  Pilih Paket Henna
                </h3>
                <div className="mb-4 p-3 bg-[#D4AF7A]/10 border border-[#D4AF7A]/30 rounded-lg">
                  <p className="text-sm text-[#F7F5F2]/80">
                    <span className="font-semibold text-[#D4AF7A]">Info:</span> Anda dapat memilih paket dari berbagai kategori dan subkategori yang berbeda dalam satu booking.
                  </p>
                </div>

                {/* Service Category Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                    Kategori Layanan *
                  </label>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {mockServices.map((service) => (
                      <button
                        key={service.id}
                        type="button"
                        onClick={() => {
                          setSelectedServiceCategory(service);
                          setSelectedSubcategory(null);
                          setBookingForm({
                            ...bookingForm,
                            serviceCategory: service.category,
                            subcategory: "",
                            package: null,
                          });
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          selectedServiceCategory?.id === service.id
                            ? "bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black"
                            : "bg-white/5 text-[#F7F5F2] hover:bg-white/10"
                        }`}
                      >
                        {service.category}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Subcategory Selection (for categories with subcategories) */}
                {selectedServiceCategory?.subcategories && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Tipe Henna *
                    </label>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                      {selectedServiceCategory.subcategories.map((sub, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => {
                            setSelectedSubcategory(sub);
                            setBookingForm({
                              ...bookingForm,
                              subcategory: sub.name,
                              package: null,
                            });
                          }}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            selectedSubcategory?.name === sub.name
                              ? "bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black"
                              : "bg-white/5 text-[#F7F5F2] hover:bg-white/10"
                          }`}
                        >
                          {sub.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Package Selection - for subcategories */}
                {selectedServiceCategory?.subcategories &&
                  selectedSubcategory?.packages && (
                    <div>
                      <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                        Pilih Paket (bisa lebih dari satu) *
                      </label>
                      <div className="space-y-3">
                        {selectedSubcategory.packages.map((pkg, idx) => {
                          const packageData = {
                            id: `${selectedServiceCategory.id}-${selectedSubcategory.name}-${idx}`,
                            name: pkg.name,
                            categoryName: selectedServiceCategory.category,
                            subcategoryName: selectedSubcategory.name,
                            type: pkg.type,
                            price: pkg.price,
                            description: pkg.features?.join(", ") || "",
                            features: pkg.features || [],
                            additionalService: pkg.additionalService || "",
                          };
                          const isSelected = isPackageSelected(packageData.id);

                          return (
                            <label
                              key={idx}
                              className={`flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition-all ${
                                isSelected
                                  ? "bg-[#D4AF7A]/20 border-[#D4AF7A]"
                                  : "bg-white/5 border-[#D4AF7A]/20 hover:border-[#D4AF7A]/40"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleItem(packageData)}
                                className="w-4 h-4 text-[#D4AF7A] rounded"
                              />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="font-medium text-[#F7F5F2]">
                                  {pkg.name}
                                </p>
                                {pkg.popular && (
                                  <span className="px-2 py-0.5 bg-[#D4AF7A] text-black text-xs font-bold rounded-full">
                                    Popular
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-[#F7F5F2]/70 mb-1">
                                {pkg.type}
                              </p>
                              {pkg.features && pkg.features.length > 0 && (
                                <ul className="text-xs text-[#F7F5F2]/60 list-disc list-inside">
                                  {pkg.features.map((feature, fidx) => (
                                    <li key={fidx}>{feature}</li>
                                  ))}
                                </ul>
                              )}
                              {pkg.additionalService && (
                                <p className="text-xs text-[#D4AF7A]/80 mt-1">
                                  + {pkg.additionalService}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-lg text-[#D4AF7A]">
                                {formatCurrency(pkg.price)}
                              </p>
                            </div>
                          </label>
                        );
                        })}
                      </div>
                    </div>
                  )}

                {/* Package Selection - for categories without subcategories */}
                {!selectedServiceCategory?.subcategories &&
                  selectedServiceCategory?.packages && (
                    <div>
                      <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                        Pilih Paket (bisa lebih dari satu) *
                      </label>
                      <div className="space-y-3">
                        {selectedServiceCategory.packages.map((pkg, idx) => {
                          const packageData = {
                            id: `${selectedServiceCategory.id}-package-${idx}`,
                            name: pkg.name,
                            categoryName: selectedServiceCategory.category,
                            subcategoryName: "",
                            type: pkg.type,
                            price: pkg.price,
                            description: pkg.features?.join(", ") || "",
                            features: pkg.features || [],
                            additionalService: pkg.additionalService || "",
                          };
                          const isSelected = isPackageSelected(packageData.id);

                          return (
                            <label
                              key={idx}
                              className={`flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition-all ${
                                isSelected
                                  ? "bg-[#D4AF7A]/20 border-[#D4AF7A]"
                                  : "bg-white/5 border-[#D4AF7A]/20 hover:border-[#D4AF7A]/40"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleItem(packageData)}
                                className="w-4 h-4 text-[#D4AF7A] rounded"
                              />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="font-medium text-[#F7F5F2]">
                                  {pkg.name}
                                </p>
                                {pkg.popular && (
                                  <span className="px-2 py-0.5 bg-[#D4AF7A] text-black text-xs font-bold rounded-full">
                                    Popular
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-[#F7F5F2]/70 mb-1">
                                {pkg.type}
                              </p>
                              {pkg.features && pkg.features.length > 0 && (
                                <ul className="text-xs text-[#F7F5F2]/60 list-disc list-inside">
                                  {pkg.features.map((feature, fidx) => (
                                    <li key={fidx}>{feature}</li>
                                  ))}
                                </ul>
                              )}
                              {pkg.additionalService && (
                                <p className="text-xs text-[#D4AF7A]/80 mt-1">
                                  + {pkg.additionalService}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-lg text-[#D4AF7A]">
                                {formatCurrency(pkg.price)}
                              </p>
                            </div>
                          </label>
                        );
                        })}
                      </div>
                    </div>
                  )}
              </div>

              {/* Selected Items Summary */}
              {bookingForm.items.length > 0 && (
                <div className="bg-[#D4AF7A]/10 border border-[#D4AF7A]/30 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-[#D4AF7A] mb-3">
                    Paket yang Dipilih ({bookingForm.items.length})
                  </h3>
                  <div className="space-y-2">
                    {bookingForm.items.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between py-2 px-3 bg-white/5 rounded-lg"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-medium text-[#F7F5F2] truncate">
                              {item.packageSnapshot?.name || 'Unknown Package'}
                            </p>
                            <span className="px-2 py-0.5 bg-[#D4AF7A]/20 text-[#D4AF7A] text-xs rounded-full shrink-0">
                              {item.packageSnapshot?.categoryName}
                            </span>
                          </div>
                          {item.packageSnapshot?.subcategoryName && (
                            <p className="text-xs text-[#F7F5F2]/60">
                              {item.packageSnapshot.subcategoryName}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <p className="text-sm font-bold text-[#D4AF7A]">
                            {formatCurrency(item.price)}
                          </p>
                          <button
                            type="button"
                            onClick={() => toggleItem(item.packageSnapshot)}
                            className="p-1 text-red-400 hover:bg-red-500/10 rounded transition-colors"
                            title="Hapus paket"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center justify-between py-2 px-3 border-t border-[#D4AF7A]/20 mt-2">
                      <p className="text-sm font-semibold text-[#F7F5F2]">Total</p>
                      <p className="text-lg font-bold text-[#D4AF7A]">
                        {formatCurrency(
                          bookingForm.items.reduce((sum, item) => sum + item.price, 0)
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Special Requests */}
              <div>
                <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                  Request Khusus
                </label>
                <textarea
                  value={bookingForm.specialRequests}
                  onChange={(e) =>
                    setBookingForm({
                      ...bookingForm,
                      specialRequests: e.target.value,
                    })
                  }
                  placeholder="Request khusus untuk acara ini (opsional)"
                  rows={3}
                  className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A] resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2.5 bg-white/5 text-[#F7F5F2] rounded-lg hover:bg-white/10 transition-colors"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="inline w-4 h-4 mr-2 animate-spin" />
                      Menyimpan...
                    </>
                  ) : (
                    "Simpan Booking"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Booking Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-[#0A0A0A] border border-[#D4AF7A]/30 rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#F7F5F2]">Edit Booking</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
                className="p-2 transition-colors rounded-lg hover:bg-white/10"
              >
                <X className="w-5 h-5 text-[#F7F5F2]" />
              </button>
            </div>

            <form onSubmit={handleUpdateBooking} className="space-y-6">
              <div className="p-4 bg-[#D4AF7A]/10 border border-[#D4AF7A]/30 rounded-lg">
                <p className="text-sm text-[#D4AF7A]">
                  <strong>Booking Number:</strong>{" "}
                  {editingBooking?.bookingNumber}
                </p>
              </div>

              {/* Client Information */}
              <div>
                <h3 className="text-lg font-semibold text-[#F7F5F2] mb-4">
                  Informasi Klien
                </h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Nama Lengkap *
                    </label>
                    <input
                      type="text"
                      required
                      value={bookingForm.clientName}
                      onChange={(e) =>
                        setBookingForm({
                          ...bookingForm,
                          clientName: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={bookingForm.clientEmail}
                      onChange={(e) =>
                        setBookingForm({
                          ...bookingForm,
                          clientEmail: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      No. WhatsApp *
                    </label>
                    <input
                      type="tel"
                      required
                      value={bookingForm.clientPhone}
                      onChange={(e) =>
                        setBookingForm({
                          ...bookingForm,
                          clientPhone: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Instagram
                    </label>
                    <input
                      type="text"
                      value={bookingForm.clientInstagram}
                      onChange={(e) =>
                        setBookingForm({
                          ...bookingForm,
                          clientInstagram: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                </div>
              </div>

              {/* Event Information */}
              <div>
                <h3 className="text-lg font-semibold text-[#F7F5F2] mb-4">
                  Informasi Acara
                </h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Tanggal Acara *
                    </label>
                    <input
                      type="date"
                      required
                      value={bookingForm.eventDate}
                      onChange={(e) =>
                        setBookingForm({
                          ...bookingForm,
                          eventDate: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Jam Mulai *
                    </label>
                    <input
                      type="time"
                      required
                      value={bookingForm.eventTime}
                      onChange={(e) =>
                        setBookingForm({
                          ...bookingForm,
                          eventTime: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Lokasi Acara *
                    </label>
                    <input
                      type="text"
                      required
                      value={bookingForm.eventLocation}
                      onChange={(e) =>
                        setBookingForm({
                          ...bookingForm,
                          eventLocation: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                </div>
              </div>

              {/* Henna Process Information */}
              <div>
                <h3 className="text-lg font-semibold text-[#F7F5F2] mb-4">
                  Informasi Proses Henna
                </h3>

                {/* Sync checkbox */}
                <div className="mb-4">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={bookingForm.sameAsEventDate}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setBookingForm({
                          ...bookingForm,
                          sameAsEventDate: checked,
                          hennaDate: checked ? bookingForm.eventDate : bookingForm.hennaDate,
                          hennaTime: checked ? bookingForm.eventTime : bookingForm.hennaTime,
                        });
                      }}
                      className="w-4 h-4 text-[#D4AF7A] rounded focus:ring-2 focus:ring-[#D4AF7A]/50"
                    />
                    <span className="text-sm text-[#F7F5F2] group-hover:text-[#D4AF7A] transition-colors">
                      Samakan dengan tanggal acara
                    </span>
                  </label>
                </div>

                {/* Henna Date & Time */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Tanggal Proses Henna *
                    </label>
                    <input
                      type="date"
                      required
                      value={bookingForm.sameAsEventDate ? bookingForm.eventDate : bookingForm.hennaDate}
                      onChange={(e) =>
                        setBookingForm({
                          ...bookingForm,
                          hennaDate: e.target.value,
                        })
                      }
                      disabled={bookingForm.sameAsEventDate}
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Jam Proses Henna *
                    </label>
                    <input
                      type="time"
                      required
                      value={bookingForm.sameAsEventDate ? bookingForm.eventTime : bookingForm.hennaTime}
                      onChange={(e) =>
                        setBookingForm({
                          ...bookingForm,
                          hennaTime: e.target.value,
                        })
                      }
                      disabled={bookingForm.sameAsEventDate}
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                </div>
              </div>

              {/* Special Requests */}
              <div>
                <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                  Request Khusus
                </label>
                <textarea
                  value={bookingForm.specialRequests}
                  onChange={(e) =>
                    setBookingForm({
                      ...bookingForm,
                      specialRequests: e.target.value,
                    })
                  }
                  placeholder="Request khusus untuk acara ini (opsional)"
                  rows={3}
                  className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A] resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2.5 bg-white/5 text-[#F7F5F2] rounded-lg hover:bg-white/10 transition-colors"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="inline w-4 h-4 mr-2 animate-spin" />
                      Menyimpan...
                    </>
                  ) : (
                    "Update Booking"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Booking Details Dialog */}
      {selectedBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-[#0A0A0A] border border-[#D4AF7A]/30 rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#F7F5F2]">
                Detail Booking
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowInvoice(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-[#D4AF7A]/10 text-[#D4AF7A] rounded-lg hover:bg-[#D4AF7A]/20 transition-colors text-sm font-medium"
                >
                  Invoice
                </button>
                {(bookingDetails?.paidAmount || 0) >= (bookingDetails?.totalAmount || 0) && (
                  <button
                    onClick={() => setShowReceipt(true)}
                    className="flex items-center gap-2 px-3 py-2 bg-green-500/10 text-green-400 rounded-lg hover:bg-green-500/20 transition-colors text-sm font-medium"
                  >
                    Bukti Pelunasan
                  </button>
                )}
                <button
                  onClick={() => {
                    setSelectedBooking(null);
                    setBookingDetails(null);
                  }}
                  className="p-2 transition-colors rounded-lg hover:bg-white/10"
                >
                  <X className="w-5 h-5 text-[#F7F5F2]" />
                </button>
              </div>
            </div>

            {loadingDetails ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#D4AF7A]" />
              </div>
            ) : bookingDetails ? (
              <div className="space-y-6">
                {/* Booking Info */}
                <div>
                  <h3 className="font-semibold text-[#F7F5F2] mb-3">
                    Informasi Booking
                  </h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-[#F7F5F2]/70">Booking Number:</span>
                      <p className="font-medium text-[#F7F5F2]">
                        {bookingDetails.bookingNumber}
                      </p>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[#F7F5F2]/70">Status:</span>
                      <span
                        className={`px-2 py-1 w-fit rounded-full text-xs font-medium ${STATUS_COLORS[bookingDetails.status]}`}
                      >
                        {getStatusLabel(bookingDetails.status)}
                      </span>
                    </div>
                    <div>
                      <span className="text-[#F7F5F2]/70">Tanggal Acara:</span>
                      <p className="font-medium text-[#F7F5F2]">
                        {formatDateShort(bookingDetails.eventDate)}
                      </p>
                    </div>
                    <div>
                      <span className="text-[#F7F5F2]/70">Jam:</span>
                      <p className="font-medium text-[#F7F5F2]">
                        {bookingDetails.eventTime || "-"}
                      </p>
                    </div>
                    <div>
                      <span className="text-[#F7F5F2]/70">Tanggal Proses Henna:</span>
                      <p className="font-medium text-[#D4AF7A]">
                        {bookingDetails.hennaDate ? formatDateShort(bookingDetails.hennaDate) : "-"}
                        {bookingDetails.hennaDate && bookingDetails.eventDate &&
                          !isSameDay(
                            bookingDetails.hennaDate instanceof Date ? bookingDetails.hennaDate : new Date(bookingDetails.hennaDate),
                            bookingDetails.eventDate instanceof Date ? bookingDetails.eventDate : new Date(bookingDetails.eventDate)
                          ) && (
                            <span className="ml-2 text-xs opacity-70">(berbeda dengan acara)</span>
                          )}
                      </p>
                    </div>
                    <div>
                      <span className="text-[#F7F5F2]/70">Jam Proses Henna:</span>
                      <p className="font-medium text-[#D4AF7A]">
                        {bookingDetails.hennaTime || bookingDetails.eventTime || "-"}
                      </p>
                    </div>
                    <div>
                      <span className="text-[#F7F5F2]/70">Deposit:</span>
                      <p className="font-medium text-[#F7F5F2]">
                        {bookingDetails.depositPercent || 30}% (
                        {formatCurrency(bookingDetails.depositAmount)})
                      </p>
                    </div>
                    <div>
                      <span className="text-[#F7F5F2]/70">Total:</span>
                      <p className="font-medium text-[#D4AF7A]">
                        {formatCurrency(bookingDetails.totalAmount)}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-[#F7F5F2]/70">Lokasi:</span>
                      <p className="font-medium text-[#F7F5F2]">
                        {bookingDetails.eventLocation || "-"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Status History Timeline */}
                {bookingDetails.statusHistory && bookingDetails.statusHistory.length > 0 ? (
                  <div>
                    <h3 className="font-semibold text-[#F7F5F2] mb-3">
                      Riwayat Status
                    </h3>
                    <div className="relative pl-6 space-y-4">
                      {/* Timeline line */}
                      <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-[#D4AF7A]/30"></div>

                      {bookingDetails.statusHistory.map((history, index) => (
                        <div key={index} className="relative">
                          {/* Timeline dot */}
                          <div className={`absolute -left-4 w-4 h-4 rounded-full border-2 ${
                            index === 0
                              ? 'bg-[#D4AF7A] border-[#D4AF7A]'
                              : 'bg-[#0A0A0A] border-[#D4AF7A]/50'
                          }`}></div>

                          <div className="text-sm">
                            <div className="flex items-center gap-2 mb-1">
                              <span
                                className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[history.status] || STATUS_COLORS[BOOKING_STATUS.PENDING_DEPOSIT]}`}
                              >
                                {getStatusLabel(history.status)}
                              </span>
                              <span className="text-[#F7F5F2]/50">
                                {history.timestamp?.toDate
                                  ? new Date(history.timestamp.toDate()).toLocaleString('id-ID')
                                  : new Date(history.timestamp).toLocaleString('id-ID')}
                              </span>
                            </div>
                            {history.reason && (
                              <p className="text-[#F7F5F2]/70">
                                Alasan: {history.reason}
                              </p>
                            )}
                            {history.changedBy && (
                              <p className="text-[#F7F5F2]/50 text-xs">
                                Oleh: {history.changedBy}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div>
                    <h3 className="font-semibold text-[#F7F5F2] mb-3">
                      Riwayat Status
                    </h3>
                    <div className="relative pl-6">
                      <div className="absolute -left-4 w-4 h-4 rounded-full border-2 bg-[#D4AF7A] border-[#D4AF7A]"></div>
                      <div className="text-sm">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[bookingDetails.status]}`}
                        >
                          {getStatusLabel(bookingDetails.status)}
                        </span>
                        <p className="text-[#F7F5F2]/50 mt-1">
                          {bookingDetails.createdAt?.toDate
                            ? new Date(bookingDetails.createdAt.toDate()).toLocaleString('id-ID')
                            : new Date(bookingDetails.createdAt).toLocaleString('id-ID')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Client Info */}
                <div>
                  <h3 className="font-semibold text-[#F7F5F2] mb-3">
                    Informasi Klien
                  </h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-[#F7F5F2]/70">Nama:</span>
                      <p className="font-medium text-[#F7F5F2]">
                        {bookingDetails.clientName}
                      </p>
                    </div>
                    <div>
                      <span className="text-[#F7F5F2]/70">Email:</span>
                      <p className="font-medium text-[#F7F5F2]">
                        {bookingDetails.clientEmail}
                      </p>
                    </div>
                    <div>
                      <span className="text-[#F7F5F2]/70">Telepon:</span>
                      <p className="font-medium text-[#F7F5F2]">
                        {bookingDetails.clientPhone}
                      </p>
                    </div>
                    <div>
                      <span className="text-[#F7F5F2]/70">Instagram:</span>
                      <p className="font-medium text-[#F7F5F2]">
                        {bookingDetails.clientInstagram || "-"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Items */}
                <div>
                  <h3 className="font-semibold text-[#F7F5F2] mb-3">
                    Item Booking
                  </h3>
                  <div className="space-y-2">
                    {bookingDetails.items?.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between p-3 text-sm rounded-lg bg-white/5"
                      >
                        <div>
                          <p className="font-medium text-[#F7F5F2]">
                            {item.packageSnapshot?.name ||
                              item.addOnSnapshot?.name}
                          </p>
                          <p className="text-[#F7F5F2]/70">
                            {item.itemType} x {item.quantity}
                          </p>
                        </div>
                        <p className="font-medium text-[#D4AF7A]">
                          {formatCurrency(item.subtotal)}
                        </p>
                      </div>
                    ))}
                    <div className="flex items-center justify-between pt-3 border-t border-[#D4AF7A]/20">
                      <span className="font-semibold text-[#F7F5F2]">
                        Total
                      </span>
                      <span className="font-bold text-lg text-[#D4AF7A]">
                        {formatCurrency(bookingDetails.totalAmount)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Payments */}
                <div>
                  <h3 className="font-semibold text-[#F7F5F2] mb-3">
                    Riwayat Pembayaran
                  </h3>
                  <div className="space-y-3">
                    {bookingDetails.payments?.length === 0 ? (
                      <p className="text-sm text-[#F7F5F2]/70">
                        Belum ada pembayaran
                      </p>
                    ) : (
                      bookingDetails.payments.map((payment) => (
                        <div
                          key={payment.id}
                          className="p-4 rounded-lg bg-white/5"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="font-medium text-sm text-[#F7F5F2]">
                                {payment.paymentNumber}
                              </p>
                              <p className="text-xs text-[#F7F5F2]/70">
                                {getPaymentTypeLabel(payment.paymentType)} -{" "}
                                {payment.paymentMethod}
                                {payment.paymentChannel &&
                                  ` (${payment.paymentChannel})`}
                              </p>
                            </div>
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${PAYMENT_STATUS_COLORS[payment.status]}`}
                            >
                              {getPaymentStatusLabel(payment.status)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-semibold text-[#F7F5F2]">
                              {formatCurrency(payment.amount)}
                            </span>
                            <div className="flex gap-2">
                              {payment.status === PAYMENT_STATUS.PENDING && (
                                <>
                                  <button
                                    onClick={() =>
                                      handleVerifyPayment(
                                        bookingDetails.id,
                                        payment.id,
                                      )
                                    }
                                    disabled={actionLoading}
                                    className="px-3 py-1 text-sm text-white transition-colors bg-green-600 rounded-lg hover:bg-green-700"
                                  >
                                    <CheckCircle className="inline w-4 h-4 mr-1" />
                                    Verifikasi
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleRejectPayment(
                                        bookingDetails.id,
                                        payment.id,
                                      )
                                    }
                                    disabled={actionLoading}
                                    className="px-3 py-1 text-sm text-red-400 transition-colors border border-red-600 rounded-lg hover:bg-red-500/10"
                                  >
                                    <XCircle className="inline w-4 h-4 mr-1" />
                                    Tolak
                                  </button>
                                </>
                              )}
                              {payment.proofImage && (
                                <button
                                  onClick={() =>
                                    window.open(payment.proofImage, "_blank")
                                  }
                                  className="px-3 py-1 bg-[#D4AF7A]/10 text-[#D4AF7A] hover:bg-[#D4AF7A]/20 rounded-lg text-sm transition-colors"
                                >
                                  <Eye className="inline w-4 h-4 mr-1" />
                                  Bukti
                                </button>
                              )}
                            </div>
                          </div>
                          {payment.rejectionReason && (
                            <p className="mt-2 text-xs text-red-400">
                              Alasan: {payment.rejectionReason}
                            </p>
                          )}
                        </div>
                      ))
                    )}
                  </div>

                  {/* Payment Summary */}
                  <div className="mt-4 pt-4 border-t border-[#D4AF7A]/20">
                    <div className="flex justify-between mb-2 text-sm">
                      <span className="text-[#F7F5F2]/70">Total Dibayar:</span>
                      <span className="font-medium text-[#F7F5F2]">
                        {formatCurrency(bookingDetails.paidAmount)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#F7F5F2]/70">Sisa:</span>
                      <span className="font-bold text-[#D4AF7A]">
                        {formatCurrency(bookingDetails.remainingAmount)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-3 pt-4 border-t border-[#D4AF7A]/20">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <button
                      onClick={() => {
                        setStatusForm({
                          newStatus: "",
                          reason: "",
                        });
                        setShowStatusModal(true);
                      }}
                      disabled={actionLoading}
                      className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
                    >
                      <Edit2 className="inline w-4 h-4 mr-2" />
                      Update Status
                    </button>
                    <button
                      onClick={() => {
                        const depositAmount =
                          bookingDetails.depositAmount ||
                          (bookingDetails.totalAmount *
                            (bookingDetails.depositPercent || 30)) /
                            100;
                        const remaining = bookingDetails.remainingAmount || 0;
                        setPaymentForm({
                          ...paymentForm,
                          amount:
                            remaining > 0
                              ? Math.min(depositAmount, remaining)
                              : depositAmount,
                        });
                        setShowPaymentModal(true);
                      }}
                      disabled={
                        actionLoading ||
                        (bookingDetails.remainingAmount || 0) <= 0
                      }
                      className="px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <CreditCard className="inline w-4 h-4 mr-2" />
                      Tambah Pembayaran
                    </button>
                  </div>

                  {bookingDetails.status === BOOKING_STATUS.CONFIRMED && (
                    <button
                      onClick={() => handleMarkCompleted(bookingDetails.id)}
                      disabled={actionLoading}
                      className="w-full px-4 py-2.5 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg transition-opacity hover:opacity-90"
                    >
                      <CheckCircle className="inline w-4 h-4 mr-2" />
                      Tandai Selesai
                    </button>
                  )}

                  {bookingDetails.status !== BOOKING_STATUS.COMPLETED &&
                    bookingDetails.status !== BOOKING_STATUS.CANCELLED && (
                      <button
                        onClick={() => handleCancelBooking(bookingDetails.id)}
                        disabled={actionLoading}
                        className="w-full px-4 py-2.5 border border-red-600 text-red-400 hover:bg-red-500/10 font-semibold rounded-lg transition-colors"
                      >
                        <XCircle className="inline w-4 h-4 mr-2" />
                        Batalkan Booking
                      </button>
                    )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Update Status Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-[#0A0A0A] border border-[#D4AF7A]/30 rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#F7F5F2]">
                Update Status Booking
              </h2>
              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setStatusForm({ newStatus: "", reason: "" });
                }}
                className="p-2 transition-colors rounded-lg hover:bg-white/10"
              >
                <X className="w-5 h-5 text-[#F7F5F2]" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                  Status Saat Ini
                </label>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[bookingDetails?.status]}`}
                >
                  {getStatusLabel(bookingDetails?.status)}
                </span>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                  Status Baru *
                </label>
                <select
                  value={statusForm.newStatus}
                  onChange={(e) =>
                    setStatusForm({ ...statusForm, newStatus: e.target.value })
                  }
                  className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] focus:outline-none focus:border-[#D4AF7A]"
                >
                  <option value="">Pilih Status</option>
                  {bookingDetails?.status !== BOOKING_STATUS.CONFIRMED && (
                    <option value={BOOKING_STATUS.CONFIRMED}>
                      Dikonfirmasi
                    </option>
                  )}
                  {bookingDetails?.status !== BOOKING_STATUS.COMPLETED && (
                    <option value={BOOKING_STATUS.COMPLETED}>Selesai</option>
                  )}
                  {bookingDetails?.status !== BOOKING_STATUS.CANCELLED && (
                    <option value={BOOKING_STATUS.CANCELLED}>Dibatalkan</option>
                  )}
                </select>
              </div>

              {statusForm.newStatus === BOOKING_STATUS.CANCELLED && (
                <div>
                  <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                    Alasan Pembatalan *
                  </label>
                  <textarea
                    value={statusForm.reason}
                    onChange={(e) =>
                      setStatusForm({ ...statusForm, reason: e.target.value })
                    }
                    placeholder="Jelaskan alasan pembatalan..."
                    rows={3}
                    className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A] resize-none"
                  />
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowStatusModal(false);
                    setStatusForm({ newStatus: "", reason: "" });
                  }}
                  className="flex-1 px-4 py-2.5 bg-white/5 text-[#F7F5F2] rounded-lg hover:bg-white/10 transition-colors"
                >
                  Batal
                </button>
                <button
                  onClick={handleUpdateStatus}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {actionLoading ? "Memproses..." : "Update"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-[#0A0A0A] border border-[#D4AF7A]/30 rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#F7F5F2]">
                Tambah Pembayaran
              </h2>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setPaymentForm({
                    paymentType: "DEPOSIT",
                    amount: "",
                    paymentMethod: "TRANSFER",
                    paymentChannel: "",
                    transactionRef: "",
                    notes: "",
                  });
                }}
                className="p-2 transition-colors rounded-lg hover:bg-white/10"
              >
                <X className="w-5 h-5 text-[#F7F5F2]" />
              </button>
            </div>

            <form onSubmit={handleAddPayment} className="space-y-4">
              <div className="p-3 bg-white/5 border border-[#D4AF7A]/20 rounded-lg text-sm">
                <div className="flex justify-between mb-2">
                  <span className="text-[#F7F5F2]/70">Total Booking:</span>
                  <span className="font-medium text-[#F7F5F2]">
                    {formatCurrency(bookingDetails?.totalAmount)}
                  </span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-[#F7F5F2]/70">Sudah Dibayar:</span>
                  <span className="font-medium text-[#F7F5F2]">
                    {formatCurrency(bookingDetails?.paidAmount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#F7F5F2]/70">Sisa:</span>
                  <span
                    className={`font-bold ${(bookingDetails?.remainingAmount || 0) <= 0 ? "text-green-400" : "text-[#D4AF7A]"}`}
                  >
                    {formatCurrency(bookingDetails?.remainingAmount)}
                  </span>
                </div>
                {(bookingDetails?.remainingAmount || 0) <= 0 && (
                  <div className="pt-2 mt-2 border-t border-green-500/30">
                    <p className="text-xs text-green-400">
                      ✓ Pembayaran sudah lunas
                    </p>
                  </div>
                )}
              </div>

              {(bookingDetails?.remainingAmount || 0) <= 0 ? (
                <div className="p-4 text-center border rounded-lg bg-green-500/10 border-green-500/30">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                  <p className="font-semibold text-green-400">
                    Pembayaran Sudah Lunas
                  </p>
                  <p className="text-sm text-[#F7F5F2]/70 mt-1">
                    Tidak dapat menambah pembayaran lagi
                  </p>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Jenis Pembayaran *
                    </label>
                    <select
                      value={paymentForm.paymentType}
                      onChange={(e) =>
                        setPaymentForm({
                          ...paymentForm,
                          paymentType: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] focus:outline-none focus:border-[#D4AF7A]"
                    >
                      <option value="DEPOSIT">Deposit</option>
                      <option value="INSTALLMENT">Cicilan</option>
                      <option value="FULL_PAYMENT">Pelunasan</option>
                      <option value="REMAINING">Pelunasan Sisa</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Jumlah *
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      max={bookingDetails?.remainingAmount || 0}
                      step="1000"
                      value={paymentForm.amount}
                      onChange={(e) =>
                        setPaymentForm({
                          ...paymentForm,
                          amount: e.target.value,
                        })
                      }
                      placeholder="Masukkan jumlah pembayaran"
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                    />
                    <p className="text-xs text-[#F7F5F2]/60 mt-1">
                      Maksimal:{" "}
                      {formatCurrency(bookingDetails?.remainingAmount)}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Metode Pembayaran *
                    </label>
                    <select
                      value={paymentForm.paymentMethod}
                      onChange={(e) =>
                        setPaymentForm({
                          ...paymentForm,
                          paymentMethod: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] focus:outline-none focus:border-[#D4AF7A]"
                    >
                      <option value="TRANSFER">Transfer</option>
                      <option value="CASH">Cash</option>
                      <option value="EWALLET">E-Wallet</option>
                      <option value="CARD">Kartu Kredit/Debit</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Channel (Opsional)
                    </label>
                    <input
                      type="text"
                      value={paymentForm.paymentChannel}
                      onChange={(e) =>
                        setPaymentForm({
                          ...paymentForm,
                          paymentChannel: e.target.value,
                        })
                      }
                      placeholder="Contoh: BCA, GoPay, OVO"
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      No. Referensi (Opsional)
                    </label>
                    <input
                      type="text"
                      value={paymentForm.transactionRef}
                      onChange={(e) =>
                        setPaymentForm({
                          ...paymentForm,
                          transactionRef: e.target.value,
                        })
                      }
                      placeholder="Nomor referensi transaksi"
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Catatan (Opsional)
                    </label>
                    <textarea
                      value={paymentForm.notes}
                      onChange={(e) =>
                        setPaymentForm({
                          ...paymentForm,
                          notes: e.target.value,
                        })
                      }
                      placeholder="Catatan tambahan..."
                      rows={2}
                      className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A] resize-none"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowPaymentModal(false);
                        setPaymentForm({
                          paymentType: "DEPOSIT",
                          amount: "",
                          paymentMethod: "TRANSFER",
                          paymentChannel: "",
                          transactionRef: "",
                          notes: "",
                        });
                      }}
                      className="flex-1 px-4 py-2.5 bg-white/5 text-[#F7F5F2] rounded-lg hover:bg-white/10 transition-colors"
                    >
                      Batal
                    </button>
                    <button
                      type="submit"
                      disabled={actionLoading}
                      className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                      {actionLoading ? "Menyimpan..." : "Simpan"}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      {showInvoice && bookingDetails && (
        <Invoice
          booking={bookingDetails}
          type="invoice"
          onClose={() => setShowInvoice(false)}
        />
      )}

      {/* Receipt Modal */}
      {showReceipt && bookingDetails && (
        <Invoice
          booking={bookingDetails}
          type="receipt"
          onClose={() => setShowReceipt(false)}
        />
      )}
    </div>
  );
};

export default AdminBookingManagement;
